'use client';
import React from 'react';
import { ChevronRight } from 'lucide-react';
import Link from 'next/link';
import { useMe } from '@/lib/auth/me';
import ThemeToggle from '@/components/ui/ThemeToggle';
import NotificationBell from '@/components/NotificationBell';
import GlobalSearch from '@/components/GlobalSearch';

interface TopbarProps {
  pageTitle: string;
  breadcrumb?: { label: string; href?: string }[];
}

export default function Topbar({ pageTitle, breadcrumb }: TopbarProps) {
  const me = useMe();

  return (
    <header className="h-14 bg-white border-b border-slate-200 flex items-center px-6 gap-4 flex-shrink-0 z-30">
      {/* Breadcrumb + Title */}
      <div className="flex-1 min-w-0">
        {breadcrumb && breadcrumb.length > 0 ? (
          <div className="flex items-center gap-1 text-sm">
            {breadcrumb.map((crumb, i) => (
              <React.Fragment key={i}>
                {i > 0 && <ChevronRight size={14} className="text-slate-300" />}
                {crumb.href && i < breadcrumb.length - 1 ? (
                  <Link
                    href={crumb.href}
                    className="text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    {crumb.label}
                  </Link>
                ) : (
                  <span
                    className={
                      i === breadcrumb.length - 1
                        ? 'text-slate-800 font-semibold'
                        : 'text-slate-400'
                    }
                  >
                    {crumb.label}
                  </span>
                )}
              </React.Fragment>
            ))}
          </div>
        ) : (
          <h1 className="text-base font-semibold text-slate-800">{pageTitle}</h1>
        )}
      </div>

      <GlobalSearch />

      <ThemeToggle />

      <NotificationBell />

      {/* User avatar */}
      <div
        className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-light to-brand text-on-fill text-xs font-bold flex items-center justify-center"
        title={`${me.name} · ${me.email}`}
      >
        {me.initials}
      </div>
    </header>
  );
}
